import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Card, Divider, Typography, Alert, Space } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  LoadingOutlined,
  SendOutlined,
  BellOutlined,
  UserAddOutlined,
  MessageOutlined,
  WifiOutlined,
  CloudOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const DebugInfo = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const user = useSelector((state) => state.user);
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString());
  const [pubnubStatus, setPubnubStatus] = useState('disconnected');
  const [testResults, setTestResults] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [testMessage, setTestMessage] = useState('Hello from debug panel!');
  const [badgeCount, setBadgeCount] = useState(0);

  // PubNub configuration
  const pubnubConfig = {
    publishKey: process.env.REACT_APP_PUBNUB_PUBLISH,
    subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE,
    userId: auth.user?.id || auth.user?.identity || 'debug-user',
    uuid: auth.user?.id || auth.user?.identity || 'debug-uuid'
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Test PubNub Connection
  const testPubNubConnection = async () => {
    setIsLoading(true);
    setTestResults(prev => ({ ...prev, connection: 'testing' }));
    
    try {
      // Simulate connection test
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (pubnubConfig.publishKey && pubnubConfig.subscribeKey) {
        setPubnubStatus('connected');
        setTestResults(prev => ({ ...prev, connection: 'success' }));
      } else {
        setPubnubStatus('error');
        setTestResults(prev => ({ ...prev, connection: 'error' }));
      }
    } catch (error) {
      setPubnubStatus('error');
      setTestResults(prev => ({ ...prev, connection: 'error' }));
    } finally {
      setIsLoading(false);
    }
  };

  // Test User Registration
  const testUserRegistration = async () => {
    setIsLoading(true);
    setTestResults(prev => ({ ...prev, registration: 'testing' }));
    
    try {
      // Simulate user registration
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const userId = auth.user?.id || auth.user?.identity;
      if (userId) {
        setTestResults(prev => ({ ...prev, registration: 'success' }));
      } else {
        setTestResults(prev => ({ ...prev, registration: 'error' }));
      }
    } catch (error) {
      setTestResults(prev => ({ ...prev, registration: 'error' }));
    } finally {
      setIsLoading(false);
    }
  };

  // Test Message Sending
  const testMessageSending = async () => {
    setIsLoading(true);
    setTestResults(prev => ({ ...prev, message: 'testing' }));
    
    try {
      // Simulate message sending
      await new Promise(resolve => setTimeout(resolve, 1000));
      setTestResults(prev => ({ ...prev, message: 'success' }));
    } catch (error) {
      setTestResults(prev => ({ ...prev, message: 'error' }));
    } finally {
      setIsLoading(false);
    }
  };

  // Test Notification Badge
  const testNotificationBadge = async () => {
    setIsLoading(true);
    setTestResults(prev => ({ ...prev, badge: 'testing' }));
    
    try {
      // Simulate badge update
      await new Promise(resolve => setTimeout(resolve, 800));
      const newCount = badgeCount + 1;
      setBadgeCount(newCount);
      setTestResults(prev => ({ ...prev, badge: 'success' }));
    } catch (error) {
      setTestResults(prev => ({ ...prev, badge: 'error' }));
    } finally {
      setIsLoading(false);
    }
  };

  // Test Local Notification
  const testLocalNotification = async () => {
    setIsLoading(true);
    setTestResults(prev => ({ ...prev, notification: 'testing' }));
    
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      
      // First check permissions
      const permission = await LocalNotifications.checkPermissions();
      console.log('🔔 Current notification permission:', permission);
      
      if (permission.display !== 'granted') {
        console.log('🔔 Requesting notification permissions...');
        const requestResult = await LocalNotifications.requestPermissions();
        console.log('🔔 Permission request result:', requestResult);
        
        if (requestResult.display !== 'granted') {
          throw new Error('Notification permission denied');
        }
      }
      
      // Create notification channel first
      try {
        await LocalNotifications.createChannel({
          id: 'chefnet_chat',
          name: 'ChefNet Chat',
          description: 'Chat notifications from ChefNet',
          importance: 4, // High importance
          visibility: 1, // Public visibility
          sound: 'default',
          vibration: true
        });
        console.log('✅ Notification channel created');
      } catch (channelError) {
        console.log('⚠️ Channel creation failed (may already exist):', channelError);
      }
      
      // Schedule the notification
      await LocalNotifications.schedule({
        notifications: [{
          id: 1001, // Use a simple integer ID
          title: 'Debug Test Notification',
          body: 'This is a test notification from the debug panel!',
          sound: 'default',
          channelId: 'chefnet_chat',
          schedule: { at: new Date(Date.now() + 1000) }
        }]
      });
      
      console.log('✅ Test notification scheduled successfully');
      setTestResults(prev => ({ ...prev, notification: 'success' }));
    } catch (error) {
      console.log('❌ Failed to show test notification:', error);
      setTestResults(prev => ({ ...prev, notification: 'error' }));
      
      // Show error details in an alert
      alert(`Notification Error: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Test PubNub Subscription
  const testPubNubSubscription = async () => {
    setIsLoading(true);
    setTestResults(prev => ({ ...prev, subscription: 'testing' }));
    
    try {
      // Import PubNub
      const Pubnub = (await import('pubnub')).default;
      
      const userId = auth.user?.id || auth.user?.identity;
      if (!userId) {
        throw new Error('No user identity available');
      }
      
      console.log('🧪 Testing PubNub subscription for user:', userId);
      
      // Create a temporary PubNub instance for testing
      const testPubnub = new Pubnub({
        publishKey: process.env.REACT_APP_PUBNUB_PUBLISH,
        subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE,
        userId: userId,
      });
      
      // Subscribe to the user's channel
      const channelName = `user_${userId}`;
      console.log('🧪 Subscribing to channel:', channelName);
      
      await testPubnub.subscribe({
        channels: [channelName],
        withPresence: true,
      });
      
      // Send a test message
      const testMessage = {
        text: 'Test message from debug panel',
        timestamp: Date.now(),
        test: true,
        sender: 'debug-panel'
      };
      
      console.log('🧪 Sending test message to channel:', channelName);
      await testPubnub.publish({
        channel: channelName,
        message: testMessage
      });
      
      // Wait a bit and then unsubscribe
      setTimeout(() => {
        testPubnub.unsubscribeAll();
        testPubnub.destroy();
      }, 2000);
      
      console.log('✅ PubNub subscription test completed successfully');
      setTestResults(prev => ({ ...prev, subscription: 'success' }));
    } catch (error) {
      console.log('❌ PubNub subscription test failed:', error);
      setTestResults(prev => ({ ...prev, subscription: 'error' }));
      alert(`Subscription Test Error: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Test Chat Notification with Action
  const testChatNotification = async () => {
    setIsLoading(true);
    setTestResults(prev => ({ ...prev, chatNotification: 'testing' }));
    
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      
      // First check permissions
      const permission = await LocalNotifications.checkPermissions();
      console.log('🔔 Current notification permission:', permission);
      
      if (permission.display !== 'granted') {
        console.log('🔔 Requesting notification permissions...');
        const requestResult = await LocalNotifications.requestPermissions();
        console.log('🔔 Permission request result:', requestResult);
        
        if (requestResult.display !== 'granted') {
          throw new Error('Notification permission denied');
        }
      }
      
      // Create notification channel first
      try {
        await LocalNotifications.createChannel({
          id: 'chefnet_chat',
          name: 'ChefNet Chat',
          description: 'Chat notifications from ChefNet',
          importance: 4, // High importance
          visibility: 1, // Public visibility
          sound: 'default',
          vibration: true
        });
        console.log('✅ Notification channel created');
      } catch (channelError) {
        console.log('⚠️ Channel creation failed (may already exist):', channelError);
      }
      
      // Schedule the chat notification with action
      await LocalNotifications.schedule({
        notifications: [{
          id: 1002, // Use a simple integer ID
          title: 'New message from Test User',
          body: 'This is a test chat message - tap to open chat',
          sound: 'default',
          channelId: 'chefnet_chat',
          schedule: { at: new Date(Date.now() + 1000) },
          actionTypeId: 'OPEN_CHAT',
          extra: {
            senderId: 'test-user-123',
            senderName: 'Test User',
            channelId: 'test-channel',
            messageType: 'chat'
          }
        }]
      });
      
      console.log('✅ Test chat notification scheduled successfully');
      setTestResults(prev => ({ ...prev, chatNotification: 'success' }));
      alert('✅ Test chat notification sent! Tap it to test navigation to chat.');
    } catch (error) {
      console.log('❌ Failed to show test chat notification:', error);
      setTestResults(prev => ({ ...prev, chatNotification: 'error' }));
      alert(`Chat Notification Error: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Test Server-Side Push Notification
  const testServerPushNotification = async () => {
    setIsLoading(true);
    setTestResults(prev => ({ ...prev, serverPush: 'testing' }));
    
    try {
      const userId = auth.user?.id || auth.user?.identity;
      if (!userId) {
        throw new Error('User ID not found');
      }

      // Call the backend test endpoint that uses PubNub's push gateway
      const response = await fetch(`${process.env.REACT_APP_API_URL}/notification/test-push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          user_identity: userId,
          message: 'This is a test PubNub push notification!'
        })
      });

      const result = await response.json();
      
      if (response.ok && result.error === false) {
        console.log('✅ PubNub push notification sent successfully');
        setTestResults(prev => ({ ...prev, serverPush: 'success' }));
        alert('✅ PubNub push notification sent! This should work even when the app is closed.\n\nClose the app completely and wait for the notification.');
      } else {
        throw new Error(result.message || 'Failed to send PubNub push notification');
      }
    } catch (error) {
      console.log('❌ Failed to send PubNub push notification:', error);
      setTestResults(prev => ({ ...prev, serverPush: 'error' }));
      alert(`PubNub Push Error: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear Badge
  const clearBadge = () => {
    setBadgeCount(0);
    setTestResults(prev => ({ ...prev, badge: 'cleared' }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'error': return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'testing': return <LoadingOutlined style={{ color: '#1890ff' }} />;
      default: return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success': return 'Success';
      case 'error': return 'Failed';
      case 'testing': return 'Testing...';
      default: return 'Not tested';
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <Card title="🔧 PubNub Debug Tools" style={{ marginBottom: '20px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          
          {/* System Status */}
          <div>
            <Title level={5}>📊 System Status</Title>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
              <div>
                <Text strong>Time:</Text> {currentTime}
              </div>
              <div>
                <Text strong>Logged In:</Text> 
                <span style={{ color: auth.isLoggedIn ? '#52c41a' : '#ff4d4f', marginLeft: '5px' }}>
                  {auth.isLoggedIn ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <Text strong>User ID:</Text> {auth.user?.id || auth.user?.identity || 'None'}
              </div>
              <div>
                <Text strong>Identity:</Text> {auth.user?.identity || 'None'}
              </div>
              <div>
                <Text strong>Phone:</Text> {auth.user?.phone_number || 'None'}
              </div>
              <div>
                <Text strong>Full Name:</Text> {auth.user?.full_name || 'None'}
              </div>
              <div>
                <Text strong>Is Chef:</Text> 
                <span style={{ color: auth.user?.is_chef ? '#52c41a' : '#1890ff', marginLeft: '5px' }}>
                  {auth.user?.is_chef ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <Text strong>Step:</Text> {auth.user?.step || 'None'}
              </div>
            </div>
            
            {!auth.isLoggedIn && (
              <Alert
                message="Authentication Required"
                description="You need to log in to test PubNub functionality. Please go back and log in first."
                type="warning"
                showIcon
                style={{ marginTop: '15px' }}
              />
            )}
            
            {auth.isLoggedIn && (
              <Alert
                message="User Authenticated"
                description={`Successfully logged in as ${auth.user?.full_name || auth.user?.phone_number || 'User'}`}
                type="success"
                showIcon
                style={{ marginTop: '15px' }}
              />
            )}
          </div>

          <Divider />

          {/* PubNub Configuration */}
          <div>
            <Title level={5}>⚙️ PubNub Configuration</Title>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '10px' }}>
              <div>
                <Text strong>Publish Key:</Text> 
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '5px', 
                  borderRadius: '4px', 
                  fontSize: '12px',
                  wordBreak: 'break-all'
                }}>
                  {pubnubConfig.publishKey ? `${pubnubConfig.publishKey.substring(0, 20)}...` : 'Not set'}
                </div>
              </div>
              <div>
                <Text strong>Subscribe Key:</Text>
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '5px', 
                  borderRadius: '4px', 
                  fontSize: '12px',
                  wordBreak: 'break-all'
                }}>
                  {pubnubConfig.subscribeKey ? `${pubnubConfig.subscribeKey.substring(0, 20)}...` : 'Not set'}
                </div>
              </div>
              <div>
                <Text strong>User ID:</Text> {pubnubConfig.userId}
              </div>
              <div>
                <Text strong>UUID:</Text> {pubnubConfig.uuid}
              </div>
            </div>
          </div>

          <Divider />

          {/* Test Controls */}
          <div>
            <Title level={5}>🧪 Test Controls</Title>
            <Space wrap>
              <Button 
                type="primary" 
                icon={<WifiOutlined />}
                onClick={testPubNubConnection}
                loading={isLoading && testResults.connection === 'testing'}
                disabled={!auth.isLoggedIn}
              >
                Test Connection
              </Button>
              
              <Button 
                type="primary" 
                icon={<UserAddOutlined />}
                onClick={testUserRegistration}
                loading={isLoading && testResults.registration === 'testing'}
                disabled={!auth.isLoggedIn}
              >
                Test Registration
              </Button>
              
              <Button 
                type="primary" 
                icon={<MessageOutlined />}
                onClick={testMessageSending}
                loading={isLoading && testResults.message === 'testing'}
                disabled={!auth.isLoggedIn}
              >
                Test Message
              </Button>
              
              <Button 
                type="primary" 
                icon={<BellOutlined />}
                onClick={testNotificationBadge}
                loading={isLoading && testResults.badge === 'testing'}
                disabled={!auth.isLoggedIn}
              >
                Test Badge ({badgeCount})
              </Button>
              
              <Button 
                type="primary" 
                icon={<BellOutlined />}
                onClick={testLocalNotification}
                loading={isLoading && testResults.notification === 'testing'}
                disabled={!auth.isLoggedIn}
              >
                Test Notification
              </Button>
              
              <Button 
                type="primary" 
                icon={<BellOutlined />}
                onClick={testChatNotification}
                loading={isLoading && testResults.chatNotification === 'testing'}
                disabled={!auth.isLoggedIn}
              >
                Test Chat Notification
              </Button>
              
              <Button 
                type="primary" 
                icon={<CloudOutlined />}
                onClick={testServerPushNotification}
                loading={isLoading && testResults.serverPush === 'testing'}
                disabled={!auth.isLoggedIn}
                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
              >
                Test Server Push
              </Button>
              
              <Button 
                type="primary" 
                icon={<WifiOutlined />}
                onClick={testPubNubSubscription}
                loading={isLoading && testResults.subscription === 'testing'}
                disabled={!auth.isLoggedIn}
                style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2' }}
              >
                Test Subscription
              </Button>
              
              <Button 
                onClick={clearBadge}
                disabled={badgeCount === 0 || !auth.isLoggedIn}
              >
                Clear Badge
              </Button>
            </Space>
          </div>

          <Divider />

          {/* Test Results */}
          <div>
            <Title level={5}>📋 Test Results</Title>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <Card size="small" title="Connection Test">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(testResults.connection)}
                  <Text>{getStatusText(testResults.connection)}</Text>
                </div>
              </Card>
              
              <Card size="small" title="User Registration">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(testResults.registration)}
                  <Text>{getStatusText(testResults.registration)}</Text>
                </div>
              </Card>
              
              <Card size="small" title="Message Sending">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(testResults.message)}
                  <Text>{getStatusText(testResults.message)}</Text>
                </div>
              </Card>
              
              <Card size="small" title="Notification Badge">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(testResults.badge)}
                  <Text>{getStatusText(testResults.badge)}</Text>
                </div>
              </Card>
              
              <Card size="small" title="Local Notification">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(testResults.notification)}
                  <Text>{getStatusText(testResults.notification)}</Text>
                </div>
              </Card>
              
              <Card size="small" title="Chat Notification">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(testResults.chatNotification)}
                  <Text>{getStatusText(testResults.chatNotification)}</Text>
                </div>
              </Card>
              
              <Card size="small" title="Server Push">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(testResults.serverPush)}
                  <Text>{getStatusText(testResults.serverPush)}</Text>
                </div>
              </Card>
              
              <Card size="small" title="PubNub Subscription">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(testResults.subscription)}
                  <Text>{getStatusText(testResults.subscription)}</Text>
                </div>
              </Card>
            </div>
          </div>

          <Divider />

          {/* Message Testing */}
          <div>
            <Title level={5}>💬 Message Testing</Title>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Enter test message"
                disabled={!auth.isLoggedIn}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  fontSize: '14px',
                  opacity: !auth.isLoggedIn ? 0.6 : 1
                }}
              />
              <Button 
                type="primary" 
                icon={<SendOutlined />}
                onClick={testMessageSending}
                loading={isLoading && testResults.message === 'testing'}
                disabled={!auth.isLoggedIn}
              >
                Send
              </Button>
            </div>
          </div>

          {/* Alerts */}
          {Object.values(testResults).some(result => result === 'error') && (
            <Alert
              message="Some tests failed"
              description="Check your PubNub configuration and network connection"
              type="warning"
              showIcon
            />
          )}
          
          {Object.values(testResults).every(result => result === 'success') && (
            <Alert
              message="All tests passed!"
              description="PubNub integration is working correctly"
              type="success"
              showIcon
            />
          )}

        </Space>
      </Card>
    </div>
  );
};

export default DebugInfo; 